export const APP_BASE = import.meta.env.BASE_URL;

export function joinBase(...segments) {
  const base = APP_BASE.endsWith('/') ? APP_BASE : `${APP_BASE}/`;
  const path = segments.filter(Boolean).join('/').replace(/^\/+/, '');
  return base + path;
}

/** CDP 静态资源，如 assets/logo-mark.png */
export function cdpUrl(relativePath = '') {
  const clean = String(relativePath).replace(/^\/?cdp\//, '').replace(/^\/+/, '');
  return joinBase('cdp', clean);
}

/** CDP 根路径，无尾部斜杠，如 /jx-picdesign/cdp */
export function cdpBase() {
  return joinBase('cdp').replace(/\/$/, '');
}

export const DEFAULT_TEMPLATE_THUMB = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" width="400" height="300">
  <defs>
    <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#f8fafc"/>
      <stop offset="100%" stop-color="#f1f5f9"/>
    </linearGradient>
    <pattern id="grid" width="16" height="16" patternUnits="userSpaceOnUse">
      <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#e2e8f0" stroke-width="0.8" stroke-dasharray="2,2"/>
    </pattern>
  </defs>
  <rect width="400" height="300" fill="url(#bgGrad)"/>
  <rect width="400" height="300" fill="url(#grid)"/>
  
  <!-- Modern Packaging Bag Outline Mockup -->
  <g transform="translate(135, 36)">
    <!-- Handles -->
    <path d="M 40 40 C 40 12, 90 12, 90 40" fill="none" stroke="#7c3aed" stroke-width="3.5" stroke-linecap="round" opacity="0.85"/>
    <!-- Bag Body with Soft Shadow -->
    <rect x="18" y="38" width="94" height="124" rx="8" fill="#ffffff" stroke="#e2e8f0" stroke-width="1.5"/>
    <!-- Die cut fold line -->
    <path d="M 18 62 L 112 62" stroke="#f1f5f9" stroke-width="1.5" stroke-dasharray="3,3"/>
    <!-- Graphic layout placeholder -->
    <rect x="32" y="76" width="66" height="66" rx="6" fill="#f8fafc" stroke="#edf2f7" stroke-width="1"/>
    <!-- Center Logo/Icon -->
    <circle cx="65" cy="109" r="14" fill="#f5f3ff"/>
    <path d="M 59 109 L 71 109 M 65 103 L 65 115" stroke="#7c3aed" stroke-width="2" stroke-linecap="round"/>
  </g>
  
  <!-- Text Labels -->
  <text x="200" y="222" font-family="-apple-system, BlinkMacSystemFont, 'PingFang SC', sans-serif" font-size="13" font-weight="600" fill="#64748b" text-anchor="middle" letter-spacing="0.5">模版设计稿预览</text>
  <text x="200" y="242" font-family="-apple-system, BlinkMacSystemFont, 'PingFang SC', sans-serif" font-size="11" fill="#94a3b8" text-anchor="middle">点击进入画布快速设计</text>
</svg>
`)}`;

/** 将 Base64 Data URL 转换为可上传的 File 对象 */
export function dataUrlToFile(dataUrl, fileName, mimeType) {
  const raw = String(dataUrl || '');
  const comma = raw.indexOf(',');
  const header = comma >= 0 ? raw.slice(0, comma) : '';
  const base64 = comma >= 0 ? raw.slice(comma + 1) : raw;
  const mime = mimeType || header.match(/:(.*?);/)?.[1] || 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], fileName || 'cover.png', { type: mime });
}

/** 将 /cdp/、相对路径等统一为带 BASE_URL 的地址 */
export function resolveCdpAsset(path) {
  if (!path || typeof path !== 'string') return DEFAULT_TEMPLATE_THUMB;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  if (path.startsWith(APP_BASE)) return path;
  if (path.startsWith('/cdp/') || path === '/cdp') return cdpUrl(path.replace(/^\/?cdp\/?/, ''));
  if (path.startsWith('cdp/')) return joinBase(path);
  if (!path.startsWith('/')) return cdpUrl(path);
  return path;
}

export function isCdpAssetPath(path) {
  if (!path || typeof path !== 'string') return false;
  if (/^(https?:|data:|blob:)/i.test(path)) return false;
  if (path.startsWith(`${APP_BASE}cdp/`)) return true;
  if (path.startsWith('/cdp/') || path.startsWith('cdp/')) return true;
  return false;
}

export function getRoleKey(loginRole) {
  const map = { admin: 'platform_admin', manager: 'store_manager', designer: 'designer', service: 'customer_service' };
  return map[loginRole] || loginRole;
}

export function cmykToCss(c, m, y, k) {
  const r = Math.round(255 * (1 - c / 100) * (1 - k / 100));
  const g = Math.round(255 * (1 - m / 100) * (1 - k / 100));
  const b = Math.round(255 * (1 - y / 100) * (1 - k / 100));
  return `rgb(${r}, ${g}, ${b})`;
}

export function clampPercent(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  return Math.round(Math.min(100, Math.max(0, n)));
}

export function cmykToRgb(c, m, y, k) {
  const C = clampPercent(c) / 100;
  const M = clampPercent(m) / 100;
  const Y = clampPercent(y) / 100;
  const K = clampPercent(k) / 100;
  return {
    r: Math.round(255 * (1 - C) * (1 - K)),
    g: Math.round(255 * (1 - M) * (1 - K)),
    b: Math.round(255 * (1 - Y) * (1 - K)),
  };
}

export function rgbToCmyk(r, g, b) {
  const rp = r / 255;
  const gp = g / 255;
  const bp = b / 255;
  const k = 1 - Math.max(rp, gp, bp);
  if (k >= 1) return { c: 0, m: 0, y: 0, k: 100 };
  return {
    c: clampPercent(((1 - rp - k) / (1 - k)) * 100),
    m: clampPercent(((1 - gp - k) / (1 - k)) * 100),
    y: clampPercent(((1 - bp - k) / (1 - k)) * 100),
    k: clampPercent(k * 100),
  };
}

export function rgbToHsv(r, g, b) {
  const rp = r / 255;
  const gp = g / 255;
  const bp = b / 255;
  const max = Math.max(rp, gp, bp);
  const min = Math.min(rp, gp, bp);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === rp) h = ((gp - bp) / d + (gp < bp ? 6 : 0)) * 60;
    else if (max === gp) h = ((bp - rp) / d + 2) * 60;
    else h = ((rp - gp) / d + 4) * 60;
  }
  return {
    h,
    s: max === 0 ? 0 : (d / max) * 100,
    v: max * 100,
  };
}

export function hsvToRgb(h, s, v) {
  const sp = Math.min(100, Math.max(0, s)) / 100;
  const vp = Math.min(100, Math.max(0, v)) / 100;
  const c = vp * sp;
  const hh = ((h % 360) + 360) % 360;
  const x = c * (1 - Math.abs((hh / 60) % 2 - 1));
  const m = vp - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hh < 60) { rp = c; gp = x; }
  else if (hh < 120) { rp = x; gp = c; }
  else if (hh < 180) { gp = c; bp = x; }
  else if (hh < 240) { gp = x; bp = c; }
  else if (hh < 300) { rp = x; bp = c; }
  else { rp = c; bp = x; }
  return {
    r: Math.round((rp + m) * 255),
    g: Math.round((gp + m) * 255),
    b: Math.round((bp + m) * 255),
  };
}

export function hsvToCmyk(h, s, v) {
  const { r, g, b } = hsvToRgb(h, s, v);
  return rgbToCmyk(r, g, b);
}

export function cmykToHsv(c, m, y, k) {
  const { r, g, b } = cmykToRgb(c, m, y, k);
  return rgbToHsv(r, g, b);
}

export function rgbToHex(r, g, b) {
  return `#${[r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
}

export function hexToCmyk(hex) {
  const raw = String(hex || '#000000').replace('#', '');
  const v = raw.length === 3 ? raw.split('').map(ch => ch + ch).join('') : raw.padStart(6, '0').slice(0, 6);
  return rgbToCmyk(
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16),
  );
}

export function getPrintConfig(data, storeId) {
  const g = data.printConfig.global;
  if (!storeId || storeId === 'all') return { ...g, colorMode: 'CMYK' };
  const s = data.printConfig.stores[storeId] || {};
  return { ...g, ...s, colorMode: 'CMYK' };
}

export function genOrderId() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const rand = String(Math.floor(Math.random() * 900) + 100);
  return `JX${date}${rand}`;
}

export function statusLabel(status) {
  const map = { done: '已定稿', draft: '草稿', cs_revision: '客服修订' };
  return map[status] || status;
}

export function statusClass(status) {
  const map = { done: 'status-done', draft: 'status-draft', cs_revision: 'status-cs' };
  return map[status] || '';
}

/** SVG 文字基线 Baseline (底部) 映射至 HTML absolute 定位 top (顶线) 的偏移校准算法 */
export function calcSvgBaselineOffset(svgY, fontSize) {
  const y = Number(svgY) || 0;
  const size = Number(fontSize) || 12;
  return y - size * 0.85;
}

